import { Badge, Box, Button, Card, Divider, Flex, Group, List, Stack, Text, Tooltip } from "@mantine/core";
import { showNotification, updateNotification } from "@mantine/notifications";
import { IconCheck, IconAlertTriangle, IconClock, IconDeviceFloppy } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import { captureException } from "@/services/log";
import { useAddNota } from "./useGetInscricao";
import useGetPergunta from "./useGetPerguntas";
import useQueryAllMedias from "./useQueryAllMedias";
import usePendingNotasStore from "../../stores/usePendingNotasStore";
import useGetProjetoFromUrl from "../cards/useGetProjetoFromUrl";

const SAVE_PENDING = "save-pending-notas";
const DEZ = 10;

interface FaseAvaliacao {
  id: string;
  titulo: string;
  checked?: boolean;
}

type ResumoAvaliacaoProperties = {
  grupos: TGruposDiagnostico[];
};

const ResumoAvaliacao = ({ grupos }: ResumoAvaliacaoProperties) => {
  const { id, idEmpresa, idEmpresaProjeto } = useParams<{
    id: string;
    idEmpresa: string;
    idEmpresaProjeto: string;
  }>();
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const { data: todasMedias, isLoading: loadingMedias } = useQueryAllMedias();
  const { data: todasPerguntas, isLoading: loadingPerguntas } = useGetPergunta({
    ids: grupos.map((g) => g.id)
  });
  const { data: projeto } = useGetProjetoFromUrl();
  const [isUpdating, setIsUpdating] = useState(false);
  const FASE_ENTREVISTA_PREFIX = "entrev";

  // Store de notas pendentes
  const pendingNotas = usePendingNotasStore((state) => state.pendingNotas);
  const clearAllPending = usePendingNotasStore((state) => state.clearAll);
  const pendingCount = usePendingNotasStore((state) => state.getPendingCount());
  const addPendingNota = usePendingNotasStore((state) => state.addPendingNota);
  const { mutateAsync: saveNota } = useAddNota();

  const fasesAtivas = useMemo<FaseAvaliacao[]>(() => {
    return (
      (projeto?.fases?.filter((f: FaseAvaliacao) => {
        const titulo = (f?.titulo as string | undefined)?.toLowerCase() ?? "";
        return f.checked && titulo.startsWith(FASE_ENTREVISTA_PREFIX);
      }) as FaseAvaliacao[]) || []
    );
  }, [projeto]);

  const empresaTargetId = idEmpresa || idEmpresaProjeto;

  const resumoDimensoes = useMemo(() => {
    if (!todasPerguntas || !todasMedias || fasesAtivas.length === 0) return [];

    const currentUser = getCurrentUser();
    if (!currentUser) return [];

    return grupos.map((grupo) => {
      const perguntasDoGrupo = todasPerguntas.filter((p) => p.grupo === grupo.id);
      const totalPerguntas = perguntasDoGrupo.length;

      const statusFases = fasesAtivas.map((fase) => {
        const notasNaFase = perguntasDoGrupo.filter((p) => {
          // Verifica se há nota salva no Firestore (APENAS do usuário logado)
          const temNotaSalva = todasMedias.some(
            (m) =>
              m.idResposta === p.id &&
              m.fase === fase.id &&
              m.idEmpresaNota === empresaTargetId &&
              m.respondente === currentUser.uid
          );

          // Verifica se há nota pendente (nova ou edição)
          const chaveNotaPendente = `${p.id}-${fase.id}`;
          const temNotaPendente = pendingNotas.has(chaveNotaPendente);

          // Considera completo se tem nota salva OU pendente
          return temNotaSalva || temNotaPendente;
        });

        return {
          faseId: fase.id,
          faseTitulo: fase.titulo,
          totalNotas: notasNaFase.length,
          completo: notasNaFase.length >= totalPerguntas
        };
      });

      return {
        id: grupo.id,
        nome: grupo.nome,
        totalPerguntas,
        statusFases
      };
    });
  }, [grupos, todasPerguntas, todasMedias, fasesAtivas, empresaTargetId, pendingNotas]);

  const totalPendencias = useMemo(() => {
    let total = 0;
    for (const dim of resumoDimensoes) {
      const pendentesNaDimensao = dim.statusFases.filter((f) => !f.completo).length;
      total += pendentesNaDimensao;
    }
    return total;
  }, [resumoDimensoes]);

  const handleFecharAvaliacao = useCallback(async () => {
    if (!activeEmpresaId || !id || !empresaTargetId) return;

    setIsUpdating(true);
    try {
      // PASSO 1: Salvar todas as notas pendentes primeiro
      if (pendingNotas.size > 0) {
        const notasPendentesArray = [...pendingNotas.values()];

        showNotification({
          title: "Salvando notas",
          message: `Salvando ${notasPendentesArray.length} nota(s) pendente(s)...`,
          color: "blue",
          loading: true,
          autoClose: false,
          id: SAVE_PENDING
        });

        // prettier-ignore
        const savePromises = notasPendentesArray.map(async (nota) =>
          nota.type === "nova"
            ? // Nota nova
            await saveNota({
              nota: nota.nota,
              justificativa: nota.justificativa,
              fase: nota.fase,
              idResposta: nota.idResposta,
              respondente: nota.respondente,
              idEmpresaNota: nota.idEmpresaNota,
              grupo: nota.grupo
            })
            : // Edição de nota existente
            await saveNota({
              ...nota.mediaOriginal,
              nota: nota.nota,
              justificativa: nota.justificativa
            })
        );

        const results = await Promise.allSettled(savePromises);

        const successCount = results.filter((r) => r.status === "fulfilled").length;
        const failedCount = results.filter((r) => r.status === "rejected").length;

        // Limpar notas pendentes apenas das que foram salvas com sucesso
        if (successCount > 0) {
          clearAllPending();
        }

        if (failedCount === 0) {
          updateNotification({
            id: SAVE_PENDING,
            title: "Notas salvas",
            message: `${successCount} nota(s) salva(s) com sucesso!`,
            color: "green",
            icon: <IconCheck size={16} />,
            loading: false,
            autoClose: 3000
          });
        } else if (successCount > 0) {
          updateNotification({
            id: SAVE_PENDING,
            title: "Salvamento parcial",
            message: `${successCount} de ${notasPendentesArray.length} nota(s) salva(s). ${failedCount} falharam.`,
            color: "yellow",
            icon: <IconAlertTriangle size={16} />,
            loading: false,
            autoClose: 5000
          });
        } else {
          updateNotification({
            id: SAVE_PENDING,
            title: "Erro ao salvar",
            message: `Nenhuma nota foi salva. Todas as ${failedCount} tentativas falharam.`,
            color: "red",
            icon: <IconAlertTriangle size={16} />,
            loading: false,
            autoClose: 5000
          });
        }
      }

      showNotification({
        title: "Notas salvas com sucesso!",
        message:
          "As médias estão sendo recalculadas. Use o ícone de atualizar ao lado das médias para ver os resultados.",
        color: "green",
        icon: <IconCheck size={16} />,
        autoClose: 5000
      });
    } catch (error) {
      captureException(error as Error, true);
      showNotification({
        title: "Erro",
        message: "Falha ao salvar notas.",
        color: "red"
      });
    } finally {
      setIsUpdating(false);
    }
  }, [activeEmpresaId, id, empresaTargetId, pendingNotas, saveNota, clearAllPending]);

  // DEV ONLY: Preenche automaticamente todas as notas com dados de teste
  const handlePreencherDadosTeste = useCallback(() => {
    if (!todasPerguntas || fasesAtivas.length === 0 || !empresaTargetId) {
      showNotification({
        title: "Erro",
        message: "Dados não carregados ainda. Aguarde um momento.",
        color: "red"
      });
      return;
    }

    const user = getCurrentUser();
    if (!user) {
      showNotification({
        title: "Erro",
        message: "Usuário não autenticado",
        color: "red"
      });
      return;
    }

    let contadorNotas = 0;

    // Para cada pergunta e cada fase, cria uma nota fake
    for (const pergunta of todasPerguntas) {
      for (const fase of fasesAtivas) {
        const chave = `${pergunta.id}-${fase.id}`;

        // Verifica se já existe nota (salva ou pendente) - apenas do usuário logado
        const jaTemNotaSalva = todasMedias?.some(
          (m) =>
            m.idResposta === pergunta.id &&
            m.fase === fase.id &&
            m.idEmpresaNota === empresaTargetId &&
            m.respondente === user.uid
        );
        const jaTemNotaPendente = pendingNotas.has(chave);

        if (!jaTemNotaSalva && !jaTemNotaPendente) {
          const notaFake = {
            type: "nova" as const,
            nota: String(Math.floor(Math.random() * DEZ) + 1), // Nota aleatória 1-10
            justificativa: `Teste automático - ${new Date().toLocaleTimeString()}`,
            fase: fase.id,
            idResposta: pergunta.id,
            respondente: user.uid,
            idEmpresaNota: empresaTargetId,
            grupo: pergunta.grupo
          };

          addPendingNota(chave, notaFake);
          contadorNotas++;
        }
      }
    }

    showNotification({
      title: "Dados de teste preenchidos!",
      message: `${contadorNotas} nota(s) adicionada(s) automaticamente 🧪`,
      color: "green",
      icon: <IconCheck size={16} />,
      autoClose: 4000
    });
  }, [todasPerguntas, fasesAtivas, empresaTargetId, todasMedias, pendingNotas, addPendingNota]);

  const handleSalvarClick = useCallback(() => {
    void handleFecharAvaliacao();
  }, [handleFecharAvaliacao]);

  if (loadingMedias || loadingPerguntas) return null;

  return (
    <Card withBorder shadow="md" style={{ flex: 1, marginBottom: 15, borderTop: "4px solid #228be6" }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Box>
            <Text size="xl" fw="bold">
              Status de Preenchimento das Notas
            </Text>
            <Text size="xs" c="dimmed">
              Verificação de completude por dimensão e fase de avaliação
            </Text>
          </Box>
          <Group gap="xs">
            {isUpdating && (
              <Badge color="cyan" variant="filled" leftSection={<IconDeviceFloppy size={14} />}>
                Salvando {pendingCount} nota(s)...
              </Badge>
            )}
            {!isUpdating && pendingCount > 0 && (
              <Badge color="orange" variant="filled" leftSection={<IconClock size={14} />}>
                {pendingCount} nota(s) aguardando salvamento
              </Badge>
            )}
            {totalPendencias > 0 ? (
              <Badge color="yellow" variant="light" leftSection={<IconAlertTriangle size={14} />}>
                {totalPendencias} Pendências identificadas
              </Badge>
            ) : (
              <Badge color="green" variant="light" leftSection={<IconCheck size={14} />}>
                Todas as notas lançadas
              </Badge>
            )}
          </Group>
        </Group>

        <Divider />

        <Box>
          <List spacing="xs" size="sm" center>
            {resumoDimensoes.map((dim) => (
              <List.Item
                key={dim.id}
                icon={
                  dim.statusFases.every((f) => f.completo) ? (
                    <IconCheck size={16} color="green" />
                  ) : (
                    <IconAlertTriangle size={16} color="orange" />
                  )
                }
              >
                <Group gap="xs">
                  <Text fw={500}>{dim.nome}:</Text>
                  {dim.statusFases.map((fase) => (
                    <Tooltip key={fase.faseId} label={`${fase.totalNotas} de ${dim.totalPerguntas} notas lançadas`}>
                      <Badge variant="dot" color={fase.completo ? "green" : "orange"} size="sm">
                        {fase.faseTitulo}: {fase.totalNotas}/{dim.totalPerguntas}
                      </Badge>
                    </Tooltip>
                  ))}
                </Group>
              </List.Item>
            ))}
          </List>
        </Box>

        <Divider />

        <Flex justify="space-between" align="center">
          {import.meta.env.DEV && (
            <Button
              leftSection={<IconAlertTriangle size={16} />}
              onClick={handlePreencherDadosTeste}
              variant="outline"
              color="orange"
              size="sm"
            >
              Preencher Dados de Teste (DEV)
            </Button>
          )}
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSalvarClick}
            loading={isUpdating}
            disabled={isUpdating}
            variant="filled"
            color="blue"
            size="md"
            style={{ marginLeft: "auto" }}
          >
            {isUpdating ? "Salvando..." : "Salvar Avaliação"}
          </Button>
        </Flex>
      </Stack>
    </Card>
  );
};

export default ResumoAvaliacao;
